import { api, type HealthSignal } from '../lib/api';
import { Alert, Badge, Card, PageHead, formatTime, useLoader } from '../components/ui';

const SEVERITY_KIND: Record<string, string> = {
  ok: 'ok',
  warning: 'warn',
  critical: 'danger',
  unknown: 'muted',
};

const SIGNAL_LABEL: Record<string, string> = {
  bounce_rate: 'Bounce rate',
  complaint_rate: 'Complaint rate',
  webhook_failure_rate: 'Webhook failures',
  stuck_messages: 'Stuck messages',
  overdue_scheduled: 'Overdue scheduled sends',
  suppression_growth: 'Suppression growth',
};

/** Why a signal exists, in the terms of what goes wrong when it fires. */
const SIGNAL_HELP: Record<string, string> = {
  bounce_rate:
    'Above 5% AWS starts reviewing the account. Compared against yesterday too — a rate that tripled overnight is a list problem on day one.',
  complaint_rate:
    'The number AWS acts on is 0.1%. One complaint in a thousand deliveries is already the ceiling.',
  webhook_failure_rate:
    'Deliveries that gave up after retrying. Your integration is out of date and nothing told you.',
  stuck_messages:
    'Queued or sending for over fifteen minutes. A queue is meant to hold things; age is what says the pipeline stopped.',
  overdue_scheduled: 'Scheduled sends past their send time.',
  suppression_growth:
    'A sudden jump usually means a bought list or a stale export was imported.',
};

const RATE_SIGNALS = new Set([
  'bounce_rate',
  'complaint_rate',
  'webhook_failure_rate',
]);

function display(signal: HealthSignal): string {
  if (signal.severity === 'unknown') return '—';
  return RATE_SIGNALS.has(signal.name) ? `${signal.value}%` : String(signal.value);
}

export function Health() {
  const { data, error, loading, reload } = useLoader(() => api.health.signals());

  const overall = data?.status ?? 'ok';

  return (
    <>
      <PageHead
        title="Health"
        subtitle="What an on-call alert would fire on, computed from this account's own last 24 hours."
        actions={
          <button type="button" className="btn btn-sm" onClick={reload} disabled={loading}>
            {loading ? 'Checking…' : 'Refresh'}
          </button>
        }
      />

      {error && <Alert kind="error">{error}</Alert>}

      {data && overall === 'ok' && (
        <Alert kind="ok">
          Nothing is firing. Signals marked <strong>unknown</strong> have too little
          traffic to grade and are not counted against you — a rate over three sends is
          noise, not a measurement.
        </Alert>
      )}
      {data && overall === 'warning' && (
        <Alert kind="warn">One or more signals are above their warning threshold.</Alert>
      )}
      {data && overall === 'critical' && (
        <Alert kind="error">
          A signal is at its critical threshold. Left alone, this is what gets a sending
          account reviewed by AWS.
        </Alert>
      )}

      <Card
        tight
        title="Signals"
        actions={
          data && (
            <span className="hint mono">checked {formatTime(data.checked_at)}</span>
          )
        }
      >
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Signal</th>
                <th style={{ textAlign: 'right' }}>Value</th>
                <th style={{ textAlign: 'right' }}>Threshold</th>
                <th style={{ textAlign: 'right' }}>Sample</th>
                <th>State</th>
              </tr>
            </thead>
            <tbody>
              {(data?.signals ?? []).map((signal) => (
                <tr key={signal.name}>
                  <td>
                    <div style={{ fontWeight: 600 }}>
                      {SIGNAL_LABEL[signal.name] ?? signal.name}
                    </div>
                    <div className="hint">{SIGNAL_HELP[signal.name] ?? ''}</div>
                    <div className="hint">{signal.summary}</div>
                  </td>
                  <td className="mono" style={{ textAlign: 'right' }}>
                    {display(signal)}
                  </td>
                  <td className="mono" style={{ textAlign: 'right' }}>
                    {RATE_SIGNALS.has(signal.name)
                      ? `${signal.threshold}%`
                      : signal.threshold}
                  </td>
                  {/* The sample is shown beside every rate on purpose: "0% of
                      4,000" and "0% of 3" are not the same statement. */}
                  <td className="mono" style={{ textAlign: 'right' }}>
                    {signal.sample_size.toLocaleString()}
                  </td>
                  <td>
                    <Badge kind={SEVERITY_KIND[signal.severity] ?? 'muted'}>
                      {signal.severity}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!data && <div className="empty">{loading ? 'Loading…' : 'No signals.'}</div>}
      </Card>
    </>
  );
}
